import type { ReactNode } from "react"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Box, Skeleton } from "@mui/material"
import type { GridSortModel } from "@mui/x-data-grid"

import type { DateRange } from "types/date.types"

import {
  selectAlertsTable,
  setAlertsTableRowsAmount,
  setAlertsTableSortModel,
} from "features/store/tablesSlice"
import useEvents from "helpers/hooks/useEvents"
import useRoutes from "helpers/hooks/useRoutes"
import {
  getAlertsFileEntityName,
  getAlertsTableColumns,
  getAlertsTableCsvFields,
  getAlertsTableRows,
  getPowerlineIdsFromEvents,
  getTowerIdsFromEvents,
} from "helpers/utils/alerts"
import DataGridTable from "widgets/common/DataGridTable"
import { selectCurrentOrgName } from "features/store/orgSlice"
import useDevices from "helpers/hooks/useDevices"
import useDeviceGroups from "helpers/hooks/useDeviceGroups"
import { formatFilename } from "helpers/utils/common"
import useSelectedPowerlines from "helpers/hooks/powerlines/useSelectedPowerlines"
import useSelectedTowers from "helpers/hooks/towers/useSelectedTowers"

const CSV_ONLY_COLUMNS_HIDDEN = {
  entityTypeCsv: false,
  entityNameCsv: false,
  entityIdCsv: false,
}

interface AlertsTableProps {
  dateRange: DateRange | null
  isDateRangeValid: boolean
  devicesIds?: number[]
  groupId?: number
  toolbarControl?: ReactNode
  emptyContent?: ReactNode
}

function AlertsTable({
  dateRange,
  isDateRangeValid,
  devicesIds,
  groupId,
  toolbarControl,
  emptyContent,
}: AlertsTableProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { baseOrgURL } = useRoutes()
  const {
    devsWRelsById,
    powerlineIdByLessId,
    towerIdByLessId,
    isLoading: devicesLoading,
  } = useDevices()
  const { groupsWRelsById } = useDeviceGroups()
  const orgName = useSelector(selectCurrentOrgName)
  const alertsTable = useSelector(selectAlertsTable)
  const { events, isLoading: eventsLoading } = useEvents({
    fromDate: dateRange?.fromDate,
    toDate: dateRange?.toDate,
    isDateRangeValid,
    devicesIds,
    groupId,
  })
  const powerlineIds = useMemo(
    () => getPowerlineIdsFromEvents(events, powerlineIdByLessId),
    [events, powerlineIdByLessId],
  )
  const { powerlinesById, powerlinesLoading } = useSelectedPowerlines({ powerlineIds })
  const towerIds = useMemo(
    () => getTowerIdsFromEvents(events, towerIdByLessId),
    [events, towerIdByLessId],
  )
  const { towersById, towersLoading } = useSelectedTowers({ towerIds })

  const fileEntityName = useMemo(
    () =>
      getAlertsFileEntityName({
        devicesIds,
        groupId,
        groupsWRelsById,
        devsWRelsById,
        powerlineIdByLessId,
        powerlinesById,
        towerIdByLessId,
        towersById,
        t,
      }),
    [
      devicesIds,
      devsWRelsById,
      groupId,
      groupsWRelsById,
      powerlineIdByLessId,
      powerlinesById,
      towerIdByLessId,
      towersById,
      t,
    ],
  )

  const columns = useMemo(
    () => getAlertsTableColumns(baseOrgURL, devicesIds, groupId, t),
    [baseOrgURL, devicesIds, groupId, t],
  )

  const csvFields = useMemo(
    () => getAlertsTableCsvFields(devicesIds, groupId),
    [devicesIds, groupId],
  )

  const rows = useMemo(
    () =>
      events && powerlineIdByLessId && powerlinesById
        ? getAlertsTableRows(
            events,
            powerlineIdByLessId,
            powerlinesById,
            towerIdByLessId,
            towersById,
            t,
          )
        : [],
    [events, powerlineIdByLessId, powerlinesById, towerIdByLessId, towersById, t],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => dispatch(setAlertsTableSortModel(model)),
    [dispatch],
  )

  return (
    <>
      {eventsLoading || powerlinesLoading || towersLoading || devicesLoading ? (
        <Box sx={{ p: 1 }}>
          <Skeleton variant="rounded" height={360} />
        </Box>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          // The entity cell stacks its name over its id.
          rowLines={columns.some(({ field }) => field === "entity") ? 2 : 1}
          toolbarActions={
            toolbarControl && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  "& .MuiButtonBase-root": { fontSize: "0.8125rem" },
                }}
              >
                {toolbarControl}
              </Box>
            )
          }
          // The date control sat before search and download, as the old toolbar ordered it.
          toolbarActionsPosition="start"
          emptyContent={emptyContent}
          csvFileName={formatFilename(
            `${orgName}_${fileEntityName}${t("alerts.EVENTS_LOG")}`,
            "csv",
          )}
          csvFields={csvFields}
          gridProps={{ columnVisibilityModel: CSV_ONLY_COLUMNS_HIDDEN }}
          sortModel={alertsTable.sortModel}
          onSortModelChange={handleSortModelChange}
          pageSize={alertsTable.qtyRows}
          onPageSizeChange={(pageSize) => dispatch(setAlertsTableRowsAmount(pageSize))}
        />
      )}
    </>
  )
}

export default AlertsTable
